import { useTanstackMutation } from 'hooks';
import { queryClient } from 'react-query';
import toast from 'react-hot-toast';
import { Campaign, CampaignType } from 'models/Campaign';
import { API_PATHS } from 'api';
import { CampaignFormType } from '../components/CampaignForm/CampaignForm.component';

type UseCampaignSubmitProps = {
  mode: 'create' | 'edit';
  initialValues?: Partial<Campaign>;
  onClose: () => void;
  isPersonal: boolean;
};

const useCampaignSubmit = ({
  mode,
  initialValues,
  onClose,
  isPersonal,
}: UseCampaignSubmitProps) => {
  const { mutateAsync: mutateCampaign, isPending: isPendingUpdate } =
    useTanstackMutation<Campaign>({
      onSuccess: (campaign) => {
        if (mode === 'edit') {
          queryClient.setQueryData(
            [`${isPersonal ? 'personal-' : ''}campaign`, { id: campaign.id }],
            campaign,
          );
        } else
          queryClient.invalidateQueries({
            queryKey: [`${isPersonal ? 'personal-' : ''}campaigns`],
          });
        toast.success(
          `Successfully ${mode === 'edit' ? 'updated' : 'created'} campaign.`,
        );
        onClose();
      },
      onError: (error) => {
        console.error(error);
        toast.error(
          `Error on ${mode === 'edit' ? 'updating' : 'creating'} campaign.`,
        );
      },
    });

  const handleSubmit = async (data: CampaignFormType) => {
    try {
      const isPersonal = data.type === CampaignType.Personal;
      const hasFile = !!data.file;

      // Select API routes based on type
      const createPath = isPersonal
        ? API_PATHS.personalCampaigns.createCampaign()
        : API_PATHS.campaigns.createCampaign();
      const updatePath = isPersonal
        ? API_PATHS.personalCampaigns.updateCampaign(initialValues?.id)
        : API_PATHS.campaigns.updateCampaign(initialValues?.id);

      const path = mode === 'edit' ? updatePath : createPath;
      const method = mode === 'edit' ? 'patch' : 'post';

      // Exclude file from campaign creation payload
      const { file, ...body } = data;

      // Create or update campaign
      const createdCampaign = await mutateCampaign({
        path,
        method,
        body,
      });

      // If personal and file exists → upload CSV
      if (isPersonal && hasFile && createdCampaign?.id) {
        const formData = new FormData();
        formData.append('file', file);
        await mutateCampaign({
          path: API_PATHS.personalCampaigns.importCsv(createdCampaign.id),
          method: 'post',
          body: formData,
        });
        toast.success('CSV imported successfully.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error during campaign submission.');
    }
  };

  return { handleSubmit, isPendingUpdate };
};

export default useCampaignSubmit;
